import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { Avatar, AvatarGroup, Box } from '@chakra-ui/react';
import { faCalendar, faClock, faClose } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { useController } from 'pages/Meetings/hooks/useController';
import theme from 'styles/themes/default';
import { formatDateTimeLabel } from 'utils/dateFormat';

import { useAlerts } from 'hooks/useAlerts';
import { useUser } from 'hooks/useUser';

import acceptInvite from 'api/matchmaking/acceptInvite';
import cancelInvite from 'api/matchmaking/cancelInvite';

import BoxInfo from 'components/atom/BoxInfo';
import Button from 'components/atom/Form/Button';
import { Wrapper } from 'components/atom/Grid';
import Loader from 'components/atom/Loader';
import SpeechBubble from 'components/atom/SpeechBubble';
import Text from 'components/atom/Text/styles';
import AvatarInvitation from 'components/molecule/AvatarInvitational';
import InvitationStats from 'components/molecule/InvitationStats';

import { Container, ContainerActions, Content, Title } from './styles';

const ContentMeeting: React.FC = () => {
  const { t } = useTranslation();
  const { toastSuccessMessage, errorMessage, confirmMessage } = useAlerts();
  const { user } = useUser();
  const {
    invitationData,
    prospectionData,
    typeInvitation,
    isLoading,
    setPageActive,
    setProspectionId,
    setInvitationDataSuggested,
  } = useController();

  const cancelInviteMutation = useMutation(cancelInvite, {
    onSuccess: (res) => {
      if (!res.success) {
        errorMessage(t('meetings:messages.cancelInvite'));
      } else {
        toastSuccessMessage(t('meetings:messages.successCancel'));
        setPageActive('schedule');
      }
    },
  });

  const acceptInviteMutation = useMutation(acceptInvite, {
    onSuccess: (res) => {
      if (!res.success) {
        errorMessage(t('meetings:messages.acceptInvite'));
      } else {
        toastSuccessMessage(t('meetings:messages.successAccept'));
        setPageActive('schedule');
      }
    },
  });

  const handleCancel = useCallback(
    (matchId: number) => {
      confirmMessage({
        message: t('meetings:messages.confirmCancel'),
        onYesCallback: () => cancelInviteMutation.mutate({ matchId }),
        confirmButtonText: t('meetings:actions.yes'),
        cancelButtonText: t('meetings:actions.no'),
      });
    },
    [cancelInviteMutation, confirmMessage, t],
  );

  return (
    <Container>
      <Content>
        {isLoading ? (
          <Loader />
        ) : (
          <Wrapper>
            {typeInvitation === 'received' ? (
              <AvatarInvitation
                type={typeInvitation}
                name={invitationData?.fullname}
                src={invitationData?.avatar}
                size="lg"
              />
            ) : (
              <AvatarGroup size="xl" max={2}>
                <Avatar name={invitationData?.fullname} src={invitationData?.avatar} />
                <Avatar name={user.fullname} src={user.avatar} />
              </AvatarGroup>
            )}

            <Box mb={10}>
              <Title>
                <strong>{invitationData?.fullname}</strong>
              </Title>

              <Text p16 lineHeight={26} textAlign="center">
                {prospectionData?.user.role}
              </Text>

              <Text weight="bold" lineHeight={18} textAlign="center">
                {prospectionData?.user.companyFantasyName}
              </Text>
            </Box>

            <Box display="flex" flexDirection="column" gap={3} width="100%">
              {typeInvitation === 'received' && (
                <SpeechBubble message={invitationData?.message || ''} />
              )}

              <InvitationStats
                status={typeInvitation}
                date={formatDateTimeLabel(
                  String(invitationData?.date || format(new Date(), 'yyyy-MM-dd HH:mm')),
                )}
                guest={invitationData?.name}
              />

              <BoxInfo label={`${t('meetings:meeting.local')}:`} info="SET Centro-Oeste" />

              {typeInvitation !== 'received' && (
                <SpeechBubble message={invitationData?.message || ''} />
              )}

              <ContainerActions>
                {typeInvitation === 'received' && (
                  <>
                    <Text weight="bold" lineHeight={26} textAlign="center">
                      {t('meetings:meeting.titleActions')}
                    </Text>

                    <Button
                      theme="purple"
                      text={t('meetings:actions.acceptInvite')}
                      icon={faCalendar}
                      disabledUppercase
                      onClick={() =>
                        acceptInviteMutation.mutate({
                          matchId: String(invitationData?.matchId),
                          dateId: String(invitationData?.date_id),
                        })
                      }
                      isLoading={acceptInviteMutation.isLoading}
                    />
                  </>
                )}

                <Button
                  theme="light-bordered"
                  text={t('meetings:actions.suggestTime')}
                  icon={faClock}
                  disabledUppercase
                  isSmall
                  onClick={() => {
                    setPageActive('invite');
                    setProspectionId(String(invitationData?.professionalId));
                    setInvitationDataSuggested(invitationData);
                  }}
                />

                <Button
                  theme="light-bordered"
                  text={t('meetings:actions.cancelMeeting')}
                  icon={faClose}
                  disabledUppercase
                  isSmall
                  onClick={() => handleCancel(Number(invitationData?.matchId))}
                  isLoading={cancelInviteMutation.isLoading}
                />

                {typeInvitation === 'received' && (
                  <Box mt={2}>
                    <Text lineHeight={20} textAlign="center" color={theme.colors.texts_light}>
                      {t('meetings:meeting.pendingInformation')}
                    </Text>
                  </Box>
                )}
              </ContainerActions>
            </Box>
          </Wrapper>
        )}
      </Content>
    </Container>
  );
};

export default ContentMeeting;
